/**
 * Configuration helpers for DAL credentials
 * 
 * Supports multiple credential sources:
 * - Environment variables (recommended for production)
 * - Direct configuration object
 * - Runtime configuration
 */

import type { DalConfig, DalConfigWithAdmin } from './client.js';

export interface SupabaseConfig {
  url: string;
  anonKey?: string;
  serviceRoleKey?: string;
  schema?: string;
}

/**
 * Load configuration from environment variables
 * 
 * Looks for:
 * - SUPABASE_URL or POSTGREST_URL
 * - SUPABASE_ANON_KEY or POSTGREST_API_KEY (Publishable key for public access, required)
 * - SUPABASE_SERVICE_ROLE_KEY (Service role key for admin access, optional)
 * - SUPABASE_SCHEMA (optional, defaults to 'littlebooker')
 * 
 * Note: SUPABASE_ANON_KEY accepts Supabase's "Publishable Key" (formerly called "anon key")
 * 
 * @returns DalConfigWithAdmin ready to pass to initClient()
 * @throws Error if required environment variables are missing
 */
export function loadConfigFromEnv(): DalConfigWithAdmin {
  const url = process.env.SUPABASE_URL || process.env.POSTGREST_URL;
  const anonKey = process.env.SUPABASE_ANON_KEY || process.env.POSTGREST_API_KEY;
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  const schema = process.env.SUPABASE_SCHEMA || 'littlebooker';

  if (!url) {
    throw new Error(
      'Missing required environment variable: SUPABASE_URL or POSTGREST_URL'
    );
  }

  // Use Publishable key (anon key) for normal operations (client-side safe)
  // Service role key should only be used server-side for admin operations
  // Supabase blocks service role keys in browser requests for security
  const apiKey = anonKey;

  if (!apiKey) {
    throw new Error(
      'Missing required environment variable: SUPABASE_ANON_KEY (Publishable key) or POSTGREST_API_KEY. ' +
      'Note: Service role keys cannot be used in browser/client-side code.'
    );
  }

  // Ensure URL includes /rest/v1 for Supabase
  const postgrestUrl = url.includes('/rest/v1') 
    ? url 
    : `${url.replace(/\/$/, '')}/rest/v1`;

  return {
    url: postgrestUrl,
    apiKey, // Uses service role key if provided, otherwise Publishable key
    adminApiKey: serviceRoleKey, // Admin client also uses service role key
    schema,
  };
}

/**
 * Create configuration from Supabase project details
 * 
 * @param projectRef - Supabase project reference (e.g., 'abcdefghijklmnop')
 * @param anonKey - Supabase Publishable key (formerly called "anon key", required)
 * @param options - Optional configuration including serviceRoleKey for admin access
 * @returns DalConfigWithAdmin ready to pass to initClient()
 */
export function createSupabaseConfig(
  projectRef: string,
  anonKey: string,
  options?: {
    serviceRoleKey?: string;
    schema?: string;
    region?: string;
  }
): DalConfigWithAdmin {
  const region = options?.region || 'supabase.co';
  const url = `https://${projectRef}.${region}/rest/v1`;

  // Use Publishable key for normal operations (client-side safe)
  const apiKey = anonKey;

  return {
    url,
    apiKey, // Uses service role key if provided, otherwise Publishable key
    adminApiKey: options?.serviceRoleKey,
    schema: options?.schema || 'littlebooker',
  };
}

/**
 * Validate that a configuration is complete
 * 
 * @param config - Configuration to validate
 * @throws Error if configuration is invalid
 */
export function validateConfig(config: DalConfig | DalConfigWithAdmin): void {
  if (!config.url) {
    throw new Error('Configuration missing: url');
  }

  try {
    new URL(config.url);
  } catch {
    throw new Error(`Invalid URL in configuration: ${config.url}`);
  }

  // API key (Publishable key) is required for normal operations
  if (!config.apiKey) {
    throw new Error('Configuration missing: apiKey (Publishable key is required)');
  }

  // Admin API key is optional but recommended for server-side admin operations
  if ('adminApiKey' in config && !config.adminApiKey) {
    console.warn('Warning: No admin API key provided. Admin operations will not be available.');
  }
}
